import { useState, useRef, useEffect } from 'react';
import { useClickOutside } from '@/hooks/useClickOutside';
import { ExpandMoreIcon } from '@/components/ui/Icons';
import { DropdownList } from '@/components/ui/DropdownList/DropdownList';
import './SelectDropdown.css';

export interface SelectDropdownOption {
  value: string;
  label: string;
}

interface SelectDropdownProps {
  options: SelectDropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  /** Компактный режим для шапок — без label, ограниченная ширина */
  compact?: boolean;
  /** Дополнительный класс для контейнера */
  className?: string;
  /** Прокрутка к активному пункту при открытии и стрелках; наведение мыши не прокручивает (см. DropdownList) */
  scrollHighlightIntoView?: boolean;
  /** Кастомный рендер выбранного значения в триггере */
  renderValue?: (selected: SelectDropdownOption | undefined) => React.ReactNode;
  /** Кастомный рендер опции в списке */
  renderOption?: (opt: SelectDropdownOption) => React.ReactNode;
  /** Класс на портал списка (DropdownList) */
  dropdownListClassName?: string;
}

export function SelectDropdown({
  options,
  value,
  onChange,
  placeholder = '— Выберите —',
  label,
  fullWidth,
  disabled,
  compact,
  className,
  scrollHighlightIntoView = true,
  renderValue,
  renderOption,
  dropdownListClassName,
}: SelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const idx = options.findIndex((o) => o.value === value);
    setHighlightIndex(Math.max(0, idx >= 0 ? idx : 0));
  }, [open, value, options]);

  useClickOutside(containerRef, () => setOpen(false), {
    excludeSelector: '[data-combo-dropdown]',
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[highlightIndex];
      if (opt) {
        onChange(opt.value);
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const displayValue = renderValue
    ? (renderValue(selected) ?? (
        <span className="select-dropdown__value select-dropdown__placeholder">{placeholder}</span>
      ))
    : selected ? (
        <span className="select-dropdown__value">{selected.label}</span>
      ) : (
        <span className="select-dropdown__value select-dropdown__placeholder">{placeholder}</span>
      );

  return (
    <div
      className={`form__field select-dropdown ${fullWidth ? 'form__field_full' : ''} ${compact ? 'select-dropdown_compact' : ''} ${className ?? ''}`}
    >
      {!compact && label && <label className="form__label">{label}</label>}
      <div
        ref={containerRef}
        className={`select-dropdown__trigger ${open ? 'select-dropdown__trigger_open' : ''} ${disabled ? 'select-dropdown__trigger_disabled' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls="select-dropdown-list"
        aria-activedescendant={open ? `select-dropdown-opt-${highlightIndex}` : undefined}
      >
        {displayValue}
        <ExpandMoreIcon size={18} className="select-dropdown__chevron" />
      </div>
      {open && options.length > 0 && (
        <DropdownList<string>
          triggerRef={containerRef}
          open={open}
          options={options.map((o) => ({ value: o.value, label: o.label, key: o.value }))}
          highlightIndex={highlightIndex}
          onSelect={(v) => {
            onChange(v);
            setOpen(false);
          }}
          onHighlightChange={setHighlightIndex}
          id="select-dropdown-list"
          scrollHighlightIntoView={scrollHighlightIntoView}
          className={dropdownListClassName}
          renderOption={renderOption ? (opt) => renderOption({ value: opt.value, label: opt.label }) : undefined}
        />
      )}
    </div>
  );
}
